import { inject, injectable } from 'inversify';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import IDeal, { IDealsPayload, IDealsWithSubscriptionPayload } from '@/creditflowresearch/domain/vo/IDeal';
import { IGetDealsResponse } from '@/creditflowresearch/domain/vo/Deals/IGetDealsResponse';
import { ISubscriptionStatus } from '@/creditflowresearch/domain/vo/ISubscriptionStatus';
import { ICorporateDeal, ICorporateDealResponse } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import type { ICorporatePayload, IMarketPagePayload } from '@/creditflowresearch/application/Deal/types';
import type { IDealMarketChartResponse, IIDealMarketChartPayload } from '@/creditflowresearch/domain/vo/MarketChart/IMarketChart';
import { withServerCache } from '@/creditflowresearch/infrastructure/cache/withServerCache';
import type { IServerCacheOptions } from '@/creditflowresearch/infrastructure/cache/withServerCache';

@injectable()
class DealRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
  ) {}

  getDictionary = async () => {
    const request = new Request('finsight_web.deal_transaction.get_dictionary');
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  };

  getDeals = async (payload: IDealsPayload | ICorporatePayload): Promise<IGetDealsResponse> => {
      const request = new Request('finsight_web.market.deal_transaction.deal.get_deals', payload);
      const response = await this.rpc.call<RpcSuccess>(request);

      return response.getResult().payload;
  };

  getCorporateDeals = async (payload: ICorporatePayload): Promise<ICorporateDealResponse> => {
    const request = new Request('finsight_web.market.deal_transaction.deal.corp.get_deals', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    const { deals, obfuscated } = response.getResult().payload;
    return {
      collection: deals.collection,
      totalCount: deals.totalCount,
      obfuscated,
    };
  };

  getCorporateDealsWithSubscription = async (payload: ICorporatePayload): Promise<{
    collection: ICorporateDeal[],
    totalCount: number,
    obfuscated: boolean,
    subscriptionStatus: ISubscriptionStatus,
  }> => {
    const request = new Request('finsight_web.market.deal_transaction.deal.corp.get_deals_with_subscription', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    const { deals, obfuscated, subscriptionStatus } = response.getResult().payload;
    return {
      collection: deals.collection,
      totalCount: deals.totalCount,
      obfuscated,
      subscriptionStatus,
    };
  };

  getDealsWithSubscriptionStatus = async (payload: IDealsWithSubscriptionPayload): Promise<{
    deals: {
      collection: IDeal[],
      totalCount: number,
    },
    subscriptionStatus: ISubscriptionStatus,
  }> => {
    const request = new Request('finsight_web.market.deal_transaction.deal.get_deals_with_subscription', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getDealsForCondorAdmin = async (payload) => {
    let response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.deal_transaction.deal.get_deals', payload));
    return response.getResult().payload;
  };

  createDeal = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.deal_transaction.deal.create', payload));
    return response.getResult().payload;
  };

  updateDeal = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.deal_transaction.deal.update', payload));
    return response.getResult().payload;
  };

  partialDealUpdate = async (payload) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.deal_transaction.deal.partial_update', payload));
    return response.getResult().payload;
  };

  archiveDealById = async (id: string = null) => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.deal_transaction.deal.archive', { id }),
    );
    return response.getResult().payload;
  };

  deleteDealById = async (id: string = null) => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.deal_transaction.deal.delete', { id }),
    );
    return response.getResult().payload;
  };

  getRankingsList = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.deal_transaction.get_rankings', payload));
    return response.getResult().payload;
  };

  getCouponTypesList = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.deal_transaction.get_coupon_types', payload));
    return response.getResult().payload;
  };

  getRegistrationsList = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.deal_transaction.get_registrations', payload));
    return response.getResult().payload;
  };

  getBenchmarksList = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.deal_transaction.benchmark.get_benchmarks', payload),
    );
    return response.getResult().payload;
  };

  getDealCardByViewId = async (viewId: number): Promise<IDeal> => {
    const request = new Request('cfr.deal.get_by_view_id', { viewId });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getDealRedirect = async (viewId: number): Promise<{
    url: string | null,
    code: '301' | '404',
  }> => {
    const request = new Request('finsight_web.deal_transaction.archived_deal.get_redirect', { viewId });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  @withServerCache({
    scope: 'Deal',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getCfrCorporateDeals(
    payload: IMarketPagePayload,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Need for decorator
    _cacheOptions?: IServerCacheOptions,
  ): Promise<IGetDealsResponse> {
    const req = new Request('cfr.deal.corp.get_all', payload);

    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withServerCache({
    scope: 'Deal',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getCorporateChartData(
    payload: IIDealMarketChartPayload,
  ): Promise<IDealMarketChartResponse> {
    const req = new Request('cfr.deal.corp.get_chart_data', payload);

    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }
}

export default DealRepository;
